/**
 * Engine releases admin page.
 *
 * Layout:
 *   PageHeader → HeroPanel (total / active / yanked / fleet adoption %)
 *              → Vertical release timeline with adoption per version
 *              → ReleaseDrawer: changelog link + adoption chart + staged
 *                rollout slider (UI hooks; backend endpoint pending)
 *              → YankDrawer: type-YANK confirmation gate
 *              → Create release drawer (form lifted from inline)
 *
 * Fleet adoption % is computed client-side: (installations on this
 * binary_version) / (total live installations). The release.version
 * isn't always identical to installation.binary_version (releases
 * publish "1.4.2" while engines may report "1.4.2-rc1"), so the match
 * is exact-string only. Good enough for a dashboard.
 */
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import {
  Boxes, RefreshCw, Plus, ShieldX, ExternalLink, GitCommit,
  AlertOctagon, Sliders, Lock, Sparkles, Pencil, Wand2, History,
} from 'lucide-react';
import {
  AdminApiClient, ApiError,
  type UpdateReleaseRow, type InstallationRow, type ReleaseType,
  type NextReleaseSuggestion, type ReleaseCandidate,
} from '../api';
import { userMessageFor, failureFrom, type Failure } from '../lib/api-state';
import { usePermissions } from '../lib/permissions';
import { AccessNotice } from '../components/ui/AccessNotice';
import { useSession } from '../session';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip, type Band } from '../components/ui/StatusChip';
import { Drawer } from '../components/ui/Drawer';
import { FetchState } from '../components/ui/FetchState';

type Severity = 'security' | 'recommended' | 'optional';
type Tier = 'starter' | 'business' | 'enterprise';

interface FormState {
  version: string;
  image: string;
  image_digest: string;
  binary_hash: string;
  severity: Severity;
  type: ReleaseType;
  available_from_tier: Tier;
  manifest_signature: string;
  min_current_version: string;
  release_notes_url: string;
  rollout_percent: number;
}

const EMPTY: FormState = {
  version: '',
  image: '',
  image_digest: '',
  binary_hash: '',
  severity: 'recommended',
  type: 'PATCH',
  available_from_tier: 'starter',
  manifest_signature: '',
  min_current_version: '',
  release_notes_url: '',
  rollout_percent: 100,
};

const TYPE_OPTIONS: ReleaseType[] = ['CRITICAL', 'PATCH', 'FEATURE', 'ENGINE'];

const TYPE_TONE: Record<ReleaseType, string> = {
  CRITICAL: '#ef4444',
  PATCH:    '#fbbf24',
  FEATURE:  '#22c55e',
  ENGINE:   '#3b82f6',
};

function bandForSeverity(sev: string): Band {
  switch (sev) {
    case 'security':    return 'critical';
    case 'recommended': return 'degraded';
    case 'optional':    return 'neutral';
    default:            return 'neutral';
  }
}

function typeChip(t: ReleaseType): JSX.Element {
  const tone = TYPE_TONE[t];
  return (
    <span
      style={{
        fontSize: '0.6875rem',
        fontWeight: 600,
        padding: '2px 6px',
        borderRadius: 4,
        color: tone,
        border: `1px solid ${tone}66`,
        background: `${tone}1a`,
        letterSpacing: '0.04em',
      }}
    >
      {t}
    </span>
  );
}

export function UpdatesPage(): JSX.Element {
  const { state } = useSession();
  const [rows, setRows] = useState<UpdateReleaseRow[] | null>(null);
  const [installations, setInstallations] = useState<InstallationRow[]>([]);
  // null = fleet size unknown (the installation list failed), distinct from an empty fleet.
  const [fleetKnown, setFleetKnown] = useState(false);
  const [err, setErr] = useState<Failure | null>(null);
  const perms = usePermissions();
  const canWrite = perms.canWrite;
  const [version, setVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [openVersion, setOpenVersion] = useState<string | null>(null);
  const [yankVersion, setYankVersion] = useState<string | null>(null);
  const [editVersion, setEditVersion] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [publishDraftVersion, setPublishDraftVersion] = useState<string | null>(null);
  const [candidate, setCandidate] = useState<ReleaseCandidate | null>(null);

  const api = useMemo(
    () => new AdminApiClient({ baseUrl: '', getToken: () => state.token }),
    [state.token],
  );

  useEffect(() => {
    setRefreshing(true);
    Promise.all([
      api.updateReleases().then((r) => r.releases)
        .catch((e) => { setErr(failureFrom(e, 'the release list')); return null; }),
      api.installations({ limit: 1000 }).then((r) => r.installations)
        .catch((e) => { failureFrom(e, 'the fleet size'); return null; }),
    ]).then(([rs, is]) => {
      if (rs) setErr(null);
      setRows(rs);
      setInstallations(is ?? []);
      setFleetKnown(is !== null);
    }).finally(() => setRefreshing(false));
  }, [api, version]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  // Release candidate — latest engine commit + computed next versions.
  // 503 (not configured) or any error just hides the card.
  useEffect(() => {
    // The candidate endpoint is owner/admin only on the server.
    if (!canWrite) { setCandidate(null); return; }
    api.releaseCandidate('beta')
      .then((c) => setCandidate(c))
      .catch((e) => { failureFrom(e, 'the release candidate'); setCandidate(null); });
  }, [api, version, canWrite]);

  const adoptionByVersion = useMemo(() => {
    const map = new Map<string, number>();
    for (const i of installations) {
      if (!i.binary_version) continue;
      map.set(i.binary_version, (map.get(i.binary_version) ?? 0) + 1);
    }
    return map;
  }, [installations]);

  const liveTotal = useMemo(
    () => installations.filter((i) => i.status === 'active' && !i.stale).length,
    [installations],
  );

  const stats = useMemo(() => {
    const total = rows?.length ?? 0;
    const active = (rows ?? []).filter((r) => !r.is_yanked).length;
    const yanked = (rows ?? []).filter((r) => r.is_yanked).length;
    const sorted = [...(rows ?? [])].sort((a, b) => Date.parse(b.released_at) - Date.parse(a.released_at));
    const latest = sorted.find((r) => !r.is_yanked);
    const latestAdoption = latest ? adoptionByVersion.get(latest.version) ?? 0 : 0;
    const adoptionPct: number | null = fleetKnown && liveTotal > 0 ? Math.round((latestAdoption / liveTotal) * 100) : null;
    return { total, active, yanked, latest, adoptionPct };
  }, [rows, adoptionByVersion, liveTotal, fleetKnown]);

  const sortedRows = useMemo(
    () => [...(rows ?? [])].sort((a, b) => Date.parse(b.released_at) - Date.parse(a.released_at)),
    [rows],
  );

  // Drafts (CI-registered, awaiting classification) get their own lane
  // above the published timeline; the timeline only shows live releases.
  const draftRows = useMemo(() => sortedRows.filter((r) => r.is_draft), [sortedRows]);
  const publishedRows = useMemo(() => sortedRows.filter((r) => !r.is_draft), [sortedRows]);

  // A draft is "superseded" when a release already published on the same
  // channel has a higher base semver — publishing it would be a
  // downgrade, so the lane disables its publish (Discard only).
  const supersededDrafts = useMemo(() => {
    const parseB = (v: string): [number, number, number] | null => {
      const m = /^(\d+)\.(\d+)\.(\d+)/.exec(v || '');
      return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
    };
    const cmp = (a: [number, number, number], b: [number, number, number]): number =>
      a[0] - b[0] || a[1] - b[1] || a[2] - b[2];
    const set = new Set<string>();
    for (const d of draftRows) {
      const db = parseB(d.version);
      if (!db) continue;
      for (const p of publishedRows.filter((x) => !x.is_yanked && x.channel === d.channel)) {
        const pb = parseB(p.version);
        if (pb && cmp(pb, db) > 0) { set.add(d.version); break; }
      }
    }
    return set;
  }, [draftRows, publishedRows]);

  return (
    <div className="page">
      <PageHeader
        title="Engine releases"
        subtitle="Publish releases, control rollout pacing, and yank known-bad builds — customers' update-check sees changes within a poll cycle."
        icon={Boxes}
        accent="#a78bfa"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw width={14} height={14}
                style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
              Refresh
            </Button>
            {canWrite && (
              <Button variant="default" size="sm" onClick={() => setCreateOpen(true)}>
                <Plus width={14} height={14} />
                Publish release
              </Button>
            )}
          </>
        }
      />

      <AccessNotice reason={canWrite ? null : `${perms.writeReason ?? ''} Publishing, editing, rolling out and yanking releases is not available to you.`} />

      {rows && <HeroPanel
        icon={Sparkles}
        accent="#a78bfa"
        title="Release pipeline"
        subtitle={
          stats.latest
            ? `Latest: ${stats.latest.version} (${stats.latest.severity}).${stats.adoptionPct === null ? '' : ` Fleet adoption: ${stats.adoptionPct}%.`}`
            : 'No releases published yet.'
        }
        kpis={[
          <HeroKpi key="t" label="Total releases" value={stats.total} hint="all-time" />,
          <HeroKpi key="a" label="Active" value={stats.active} band={stats.active > 0 ? 'healthy' : 'neutral'} />,
          <HeroKpi key="y" label="Yanked" value={stats.yanked}
                   band={stats.yanked > 0 ? 'critical' : 'neutral'} />,
          <HeroKpi
            key="r"
            label="Latest adoption"
            value={stats.adoptionPct === null ? '—' : `${stats.adoptionPct}%`}
            band={
              stats.adoptionPct === null ? 'neutral'
              : stats.adoptionPct >= 80 ? 'healthy'
              : stats.adoptionPct >= 30 ? 'degraded'
              : 'warning'
            }
            hint={!fleetKnown ? 'fleet size unavailable' : stats.latest ? `vs live fleet of ${liveTotal}` : 'no live fleet yet'}
          />,
        ]}
      />}

      {candidate?.configured && (
        <CutReleaseCard api={api} candidate={candidate} onCut={onRefresh} refreshSignal={version} />
      )}

      {draftRows.length > 0 && (
        <DraftReleasesLane
          rows={draftRows}
          superseded={supersededDrafts}
          canWrite={canWrite}
          onPublish={setPublishDraftVersion}
          onDiscard={(v) => {
            void api.discardDraft(v)
              .then(onRefresh)
              .catch((e) => { alert(`Could not discard ${v}. ${userMessageFor(e)}`); onRefresh(); });
          }}
        />
      )}

      <div style={{ marginTop: 16 }}>
        <FetchState
          loading={!rows && !err}
          error={err}
          empty={!!rows && publishedRows.length === 0 && draftRows.length === 0}
          emptyTitle="No releases yet"
          emptyDescription="Push a tagged commit — the CI pipeline builds, signs, and drops the new version here to classify. Or publish manually with the button up top."
          onRetry={onRefresh}
        >
          <ReleaseTimeline
            rows={publishedRows}
            adoption={adoptionByVersion}
            fleetSize={liveTotal}
            canWrite={canWrite}
            onOpen={setOpenVersion}
            onYank={setYankVersion}
            onEdit={setEditVersion}
          />
        </FetchState>
      </div>

      <ReleaseDrawer
        api={api}
        version={openVersion}
        rows={rows ?? []}
        adoption={adoptionByVersion}
        fleetSize={liveTotal}
        canWrite={canWrite}
        onClose={() => setOpenVersion(null)}
        onYank={() => { if (openVersion) setYankVersion(openVersion); }}
        onEdit={() => { if (openVersion) setEditVersion(openVersion); }}
        onRolloutChanged={onRefresh}
      />

      <YankDrawer
        api={api}
        version={yankVersion}
        onClose={() => setYankVersion(null)}
        onYanked={() => { setYankVersion(null); onRefresh(); }}
      />

      <CreateDrawer
        api={api}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); onRefresh(); }}
      />

      <EditDrawer
        api={api}
        version={editVersion}
        rows={rows ?? []}
        onClose={() => setEditVersion(null)}
        onSaved={() => { setEditVersion(null); onRefresh(); }}
      />

      <PublishDraftDrawer
        api={api}
        version={publishDraftVersion}
        rows={rows ?? []}
        onClose={() => setPublishDraftVersion(null)}
        onPublished={() => { setPublishDraftVersion(null); onRefresh(); }}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Vertical release timeline
 * ────────────────────────────────────────────────────────────────── */

function ReleaseTimeline({
  rows, adoption, fleetSize, canWrite, onOpen, onYank, onEdit,
}: {
  rows: UpdateReleaseRow[];
  canWrite: boolean;
  adoption: Map<string, number>;
  fleetSize: number;
  onOpen: (v: string) => void;
  onYank: (v: string) => void;
  onEdit: (v: string) => void;
}): JSX.Element {
  return (
    <ol
      style={{
        listStyle: 'none', padding: 0, margin: 0,
        position: 'relative',
        display: 'flex', flexDirection: 'column', gap: 0,
      }}
    >
      {rows.map((r, i) => (
        <ReleaseTimelineItem
          key={r.version}
          row={r}
          adoption={adoption.get(r.version) ?? 0}
          fleetSize={fleetSize}
          isLatest={i === 0}
          isLast={i === rows.length - 1}
          canWrite={canWrite}
          onOpen={() => onOpen(r.version)}
          onYank={() => onYank(r.version)}
          onEdit={() => onEdit(r.version)}
        />
      ))}
    </ol>
  );
}

function ReleaseTimelineItem({
  row, adoption, fleetSize, isLatest, isLast, canWrite, onOpen, onYank, onEdit,
}: {
  row: UpdateReleaseRow;
  canWrite: boolean;
  adoption: number;
  fleetSize: number;
  isLatest: boolean;
  isLast: boolean;
  onOpen: () => void;
  onYank: () => void;
  onEdit: () => void;
}): JSX.Element {
  const tone = row.is_yanked ? '#94a3b8'
            : row.severity === 'security' ? '#ef4444'
            : row.severity === 'recommended' ? '#fbbf24'
                                             : '#22d3ee';
  const adoptionPct = fleetSize > 0 ? Math.round((adoption / fleetSize) * 100) : 0;

  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '32px minmax(0, 1fr)',
        gap: 12,
        position: 'relative',
        paddingBottom: isLast ? 0 : 12,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <span
          style={{
            width: 32, height: 32,
            borderRadius: 999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `${tone}1f`,
            border: `2px solid ${tone}`,
            color: tone,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <GitCommit width={14} height={14} />
        </span>
        {!isLast && (
          <span
            aria-hidden
            style={{
              flex: 1,
              width: 2,
              background: 'var(--border)',
              marginTop: -2,
              marginBottom: -2,
            }}
          />
        )}
      </div>

      <div
        onClick={onOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          // Only the card itself; Enter on the Edit/Yank buttons inside must not also open it.
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); }
        }}
        style={{
          padding: 14,
          borderRadius: 12,
          border: `1px solid ${isLatest ? tone : 'var(--border)'}`,
          background: row.is_yanked ? 'var(--bg-overlay)' : 'var(--bg-raised)',
          opacity: row.is_yanked ? 0.7 : 1,
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <code
            style={{
              fontSize: '0.875rem', fontWeight: 600,
              padding: '2px 8px', borderRadius: 6,
              background: 'var(--bg-overlay)', border: '1px solid var(--border)',
            }}
          >
            {row.version}
          </code>
          <StatusChip band={bandForSeverity(row.severity)} size="sm">
            {row.severity}
          </StatusChip>
          {row.type && typeChip(row.type)}
          <span
            style={{
              fontSize: '0.6875rem',
              color: 'var(--text-muted)',
              padding: '2px 6px',
              borderRadius: 4,
              border: '1px solid var(--border)',
              background: 'var(--bg-overlay)',
            }}
          >
            from {row.available_from_tier}
          </span>
          {row.rollout_percent < 100 && !row.is_yanked && (
            <span
              title="Staged rollout in progress"
              style={{
                fontSize: '0.6875rem',
                fontWeight: 600,
                padding: '2px 6px',
                borderRadius: 4,
                color: '#a78bfa',
                border: '1px solid rgba(167, 139, 250, 0.4)',
                background: 'rgba(167, 139, 250, 0.1)',
              }}
            >
              rolling out · {row.rollout_percent}%
            </span>
          )}
          {row.is_yanked && (
            <StatusChip band="critical" size="sm" pulse>yanked</StatusChip>
          )}
          {isLatest && !row.is_yanked && (
            <StatusChip band="healthy" size="sm" noDot>latest</StatusChip>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
            {fmtRelative(row.released_at)}
          </span>
        </div>

        <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 200px', alignItems: 'center' }}>
          <div>
            {row.is_yanked && row.yanked_reason ? (
              <p style={{
                margin: 0, fontSize: '0.75rem', color: 'var(--danger)',
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <AlertOctagon width={11} height={11} />
                {row.yanked_reason}
              </p>
            ) : (
              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {row.min_current_version
                  ? `Skips installs older than ${row.min_current_version}`
                  : 'Available to all eligible installs'}
              </p>
            )}
          </div>
          <AdoptionBar pct={adoptionPct} count={adoption} fleet={fleetSize} tone={tone} />
        </div>

        <div
          style={{
            marginTop: 10, paddingTop: 10,
            borderTop: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {row.release_notes_url && (
            <a
              href={row.release_notes_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: '0.75rem', color: 'var(--accent-text)',
                textDecoration: 'none',
              }}
            >
              <ExternalLink width={11} height={11} />
              Release notes
            </a>
          )}
          {canWrite && !row.is_yanked && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={onEdit}
                style={{ marginLeft: 'auto' }}
              >
                <Pencil width={12} height={12} />
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onYank}
              >
                <ShieldX width={12} height={12} />
                Yank
              </Button>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

function AdoptionBar({
  pct, count, fleet, tone,
}: { pct: number; count: number; fleet: number; tone: string }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
        <span>Fleet adoption</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {count} / {fleet} ({pct}%)
        </span>
      </div>
      <div
        style={{
          height: 6,
          borderRadius: 999,
          background: 'var(--bg-overlay)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${Math.min(100, pct)}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${tone}, ${tone}aa)`,
            transition: 'width 240ms ease',
          }}
        />
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Per-release detail drawer (changelog + adoption + rollout slider)
 * ────────────────────────────────────────────────────────────────── */

function ReleaseDrawer({
  api, version, rows, adoption, fleetSize, canWrite, onClose, onYank, onEdit, onRolloutChanged,
}: {
  api: AdminApiClient;
  canWrite: boolean;
  version: string | null;
  rows: UpdateReleaseRow[];
  adoption: Map<string, number>;
  fleetSize: number;
  onClose: () => void;
  onYank: () => void;
  onEdit: () => void;
  onRolloutChanged: () => void;
}): JSX.Element {
  const row = useMemo(() => rows.find((r) => r.version === version) ?? null, [rows, version]);
  const [rollout, setRollout] = useState<number>(100);
  const [rolloutBusy, setRolloutBusy] = useState(false);
  const [rolloutErr, setRolloutErr] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Phase A (2026-05-14): mandatory toggle + promote action state.
  const [mandatoryBusy, setMandatoryBusy] = useState(false);
  const [mandatoryErr, setMandatoryErr] = useState<string | null>(null);
  const [promoteTo, setPromoteTo] = useState<string>('');
  const [promoteBusy, setPromoteBusy] = useState(false);
  const [promoteErr, setPromoteErr] = useState<string | null>(null);

  const toggleMandatory = async (next: boolean): Promise<void> => {
    if (!row) return;
    setMandatoryBusy(true);
    setMandatoryErr(null);
    try {
      await api.setUpdateMandatory(row.version, next);
      onRolloutChanged(); // refresh row list
    } catch (e) {
      setMandatoryErr(userMessageFor(e));
    } finally {
      setMandatoryBusy(false);
    }
  };

  const submitPromote = async (): Promise<void> => {
    if (!row || !promoteTo) return;
    setPromoteBusy(true);
    setPromoteErr(null);
    try {
      await api.promoteUpdateRelease(row.version, promoteTo as 'internal' | 'alpha' | 'beta' | 'rc' | 'stable');
      setPromoteTo('');
      onRolloutChanged();
    } catch (e) {
      if (e instanceof ApiError && e.body?.error === 'already_in_channel') {
        setPromoteErr(`Already in ${promoteTo}`);
      } else {
        setPromoteErr(userMessageFor(e));
      }
    } finally {
      setPromoteBusy(false);
    }
  };

  // Reset slider whenever a different release is opened — preferring
  // the row's stored rollout_percent over the legacy "100" hardcode.
  useEffect(() => {
    if (row) setRollout(row.is_yanked ? 0 : (row.rollout_percent ?? 100));
    setRolloutErr(null);
  }, [row?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced PUT to /rollout — fire 400ms after the operator stops
  // dragging the slider so a slow drag doesn't spray dozens of writes.
  // Skips the initial reset effect (rollout still equals row value).
  useEffect(() => {
    if (!row) return;
    if (rollout === row.rollout_percent) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setRolloutBusy(true);
      setRolloutErr(null);
      api.setUpdateRolloutPercent(row.version, rollout)
        .then(() => onRolloutChanged())
        .catch((e) => {
          setRolloutErr(userMessageFor(e));
          // The server kept the old value; put the slider back so it never
          // shows a rollout that was not applied.
          setRollout(row.rollout_percent ?? 100);
        })
        .finally(() => setRolloutBusy(false));
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rollout]);

  const adoptionCount = row ? adoption.get(row.version) ?? 0 : 0;
  const adoptionPct = fleetSize > 0 ? Math.round((adoptionCount / fleetSize) * 100) : 0;

  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      width="lg"
      title={
        row ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <GitCommit width={14} height={14} />
            <code>{row.version}</code>
          </span>
        ) : ''
      }
      subtitle={row ? `${row.severity} · from ${row.available_from_tier} tier` : undefined}
      footer={
        row && canWrite && !row.is_yanked && (
          <>
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Pencil width={14} height={14} />
              Edit
            </Button>
            <Button variant="destructive" size="sm" onClick={onYank}>
              <ShieldX width={14} height={14} />
              Yank…
            </Button>
          </>
        )
      }
    >
      {row && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Section title="Release">
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <KV label="Severity">
                <StatusChip band={bandForSeverity(row.severity)}>{row.severity}</StatusChip>
              </KV>
              <KV label="Tier">{row.available_from_tier}</KV>
              <KV label="Released">{row.released_at.slice(0, 16).replace('T', ' ')}Z</KV>
              <KV label="Min current">{row.min_current_version ?? <span style={{ color: 'var(--text-muted)' }}>any</span>}</KV>
              <KV label="State">
                {row.is_yanked
                  ? <StatusChip band="critical" size="sm" pulse>yanked</StatusChip>
                  : <StatusChip band="healthy" size="sm" noDot>active</StatusChip>}
              </KV>
              {row.release_notes_url && (
                <KV label="Notes">
                  <a
                    href={row.release_notes_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: '0.75rem', color: 'var(--accent-text)',
                      textDecoration: 'none',
                    }}
                  >
                    <ExternalLink width={11} height={11} />
                    open
                  </a>
                </KV>
              )}
            </div>
          </Section>

          {row.is_yanked && row.yanked_reason && (
            <Section title="Yank reason">
              <p
                style={{
                  margin: 0, fontSize: '0.875rem',
                  padding: 12, borderRadius: 8,
                  background: 'var(--danger-soft)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: 'var(--danger)',
                }}
              >
                {row.yanked_reason}
              </p>
            </Section>
          )}

          <Section title="Fleet adoption">
            <AdoptionBar
              pct={adoptionPct}
              count={adoptionCount}
              fleet={fleetSize}
              tone="#a78bfa"
            />
            <p style={{ margin: '8px 0 0', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
              Counts engines reporting <code>{row.version}</code> as their binary_version against the live (active, non-stale) fleet.
            </p>
          </Section>

          <Section
            title="Staged rollout"
            hint={
              rolloutErr
                ? `Save failed: ${rolloutErr}`
                : rolloutBusy
                  ? 'Saving…'
                  : 'Customer engines whose hash falls below this percentage receive the release.'
            }
          >
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: 12, borderRadius: 10,
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
              }}
            >
              <Sliders width={14} height={14} color="var(--text-muted)" />
              <input
                type="range" min={0} max={100} step={5}
                value={rollout}
                onChange={(e) => setRollout(Number(e.target.value))}
                disabled={!canWrite || row.is_yanked || rolloutBusy}
                style={{ flex: 1 }}
              />
              <span
                style={{
                  fontSize: '0.875rem',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 600,
                  color: rolloutErr ? 'var(--danger)' : 'var(--text-primary)',
                  minWidth: 56, textAlign: 'right',
                }}
              >
                {rollout}%
              </span>
            </div>
          </Section>

          <Section
            title="Channel"
            hint={
              promoteErr
                ? `Promote failed: ${promoteErr}`
                : promoteBusy
                  ? 'Promoting…'
                  : row.promoted_from_channel && row.promoted_at
                    ? `Last promoted from ${row.promoted_from_channel} on ${row.promoted_at.slice(0, 10)}`
                    : 'Move this release between channels (e.g. beta → stable).'
            }
          >
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: 12, borderRadius: 10,
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
              }}
            >
              <StatusChip band="neutral" size="sm">{row.channel}</StatusChip>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>→</span>
              <select
                value={promoteTo}
                onChange={(e) => setPromoteTo(e.target.value)}
                disabled={!canWrite || row.is_yanked || promoteBusy}
                style={{
                  flex: 1, padding: '6px 8px',
                  borderRadius: 6, border: '1px solid var(--border)',
                  background: 'var(--bg-base)', color: 'var(--text-primary)',
                  fontSize: '0.8125rem',
                }}
              >
                <option value="">(promote to…)</option>
                {(['internal', 'alpha', 'beta', 'rc', 'stable'] as const)
                  .filter((c) => c !== row.channel)
                  .map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <Button
                variant="secondary" size="sm"
                onClick={() => void submitPromote()}
                disabled={!canWrite || !promoteTo || row.is_yanked || promoteBusy}
              >
                Promote
              </Button>
            </div>
          </Section>

          <Section
            title="Mandatory"
            hint={
              mandatoryErr
                ? `Toggle failed: ${mandatoryErr}`
                : mandatoryBusy
                  ? 'Saving…'
                  : row.mandatory
                    ? 'Customer engines will force-apply this release past the deadline regardless of auto-update policy.'
                    : 'Toggle ON to require engines to apply this release past a deadline.'
            }
          >
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: 12, borderRadius: 10,
                background: row.mandatory ? 'var(--danger-soft)' : 'var(--bg-overlay)',
                border: `1px solid ${row.mandatory ? 'rgba(239, 68, 68, 0.4)' : 'var(--border)'}`,
              }}
            >
              <StatusChip band={row.mandatory ? 'critical' : 'neutral'} size="sm">
                {row.mandatory ? 'mandatory' : 'optional'}
              </StatusChip>
              <span style={{ flex: 1, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {row.mandatory ? 'Force-apply enabled' : 'Operator-driven apply only'}
              </span>
              <Button
                variant={row.mandatory ? 'destructive' : 'secondary'}
                size="sm"
                onClick={() => void toggleMandatory(!row.mandatory)}
                disabled={!canWrite || row.is_yanked || mandatoryBusy}
              >
                {row.mandatory ? 'Unset' : 'Set mandatory'}
              </Button>
            </div>
          </Section>

          <Section title="Identifiers">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              <div>image: <code>{row.image}</code></div>
              <div>image_digest: <code>{row.image_digest.slice(0, 32)}…</code></div>
              <div>binary_hash: <code>{row.binary_hash.slice(0, 24)}…</code></div>
              <div>manifest_signature: <code>{row.manifest_signature.slice(0, 24)}…</code></div>
            </div>
          </Section>
        </div>
      )}
    </Drawer>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Yank confirmation drawer with type-YANK gate
 * ────────────────────────────────────────────────────────────────── */

function YankDrawer({
  api, version, onClose, onYanked,
}: {
  api: AdminApiClient;
  version: string | null;
  onClose: () => void;
  onYanked: () => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const lastRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastRef.current === version) return;
    lastRef.current = version;
    setReason('');
    setConfirmation('');
    setErr(null);
  }, [version]);

  const submit = async (): Promise<void> => {
    if (!version) return;
    if (confirmation !== 'YANK') {
      setErr('Type YANK exactly to confirm.');
      return;
    }
    if (!reason.trim()) {
      setErr('A reason is required (customers will see it).');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.yankUpdateRelease(version, reason.trim());
      onYanked();
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={!!version}
      onClose={onClose}
      width="md"
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--danger)' }}>
          <ShieldX width={14} height={14} />
          Yank release
        </span>
      }
      subtitle={version ? `Pulling ${version} from update-check responses` : undefined}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => void submit()}
            disabled={busy || confirmation !== 'YANK' || !reason.trim()}
          >
            <ShieldX width={14} height={14} />
            {busy ? 'Yanking…' : 'Confirm yank'}
          </Button>
        </>
      }
    >
      {version && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p
            style={{
              margin: 0, padding: 12, borderRadius: 8,
              background: 'var(--danger-soft)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: 'var(--danger)',
              fontSize: '0.8125rem',
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <AlertOctagon width={14} height={14} />
            Yanking <code style={{ color: 'inherit' }}>{version}</code> immediately stops serving
            it via /v1/updates/check. Customers already on this version stay on it.
          </p>

          <Field label="Reason" hint="Visible to customers via the update-check API.">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Critical regression in patch streaming – use 1.4.1 instead."
              style={inputStyle}
            />
          </Field>

          <Field label="Type YANK to confirm" hint="Caps. Exactly four letters.">
            <input
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder="YANK"
              style={{
                ...inputStyle,
                fontFamily: 'ui-monospace, monospace',
                letterSpacing: '0.18em',
              }}
            />
          </Field>

          {err && <p className="err" style={{ fontSize: '0.8125rem' }}>{err}</p>}

          <p style={{ margin: 0, fontSize: '0.6875rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Lock width={11} height={11} />
            Action persisted to the audit log (actor + reason).
          </p>
        </div>
      )}
    </Drawer>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Create release wizard — 3 steps, with a sticky footer that shows
 * which step you're on and a Back / Next button pair. The artifact
 * identity (version/image/digests/sig) is the "create the package"
 * step; classification + rollout follow; final step is a review.
 *
 * The wizard state is the same FormState the old single-page drawer
 * used — we only re-flow the layout. Submit still hits POST
 * /internal/update-releases atomically.
 * ────────────────────────────────────────────────────────────────── */

const WIZARD_STEPS = [
  { id: 'package',        label: '1. Package',        hint: 'Version + image + signed manifest' },
  { id: 'classification', label: '2. Classification', hint: 'Severity + type + tier' },
  { id: 'rollout',        label: '3. Rollout',        hint: 'Stage + notes + review' },
] as const;
type WizardStep = typeof WIZARD_STEPS[number]['id'];

function CreateDrawer({
  api, open, onClose, onCreated,
}: {
  api: AdminApiClient;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [step, setStep] = useState<WizardStep>('package');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<NextReleaseSuggestion | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY); setStep('package'); setErr(null); setSuggestion(null);
    api.nextReleaseSuggestion()
      .then((s) => setSuggestion(s))
      .catch(() => setSuggestion(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Apply the entire suggestion (version + image template + defaults +
  // dev placeholders when present). Operator can still edit anything.
  const autoFillFromSuggestion = (): void => {
    if (!suggestion) return;
    setForm((f) => ({
      ...f,
      version: suggestion.suggested_version,
      image: f.image || (suggestion.image_template
        ? suggestion.image_template + suggestion.suggested_version
        : ''),
      severity: suggestion.defaults.severity,
      type: suggestion.defaults.type,
      available_from_tier: suggestion.defaults.available_from_tier,
      // Only fill the crypto fields if the operator hasn't typed
      // anything AND the server returned dev-mode placeholders.
      image_digest: f.image_digest || (suggestion.placeholders?.image_digest ?? ''),
      binary_hash: f.binary_hash || (suggestion.placeholders?.binary_hash ?? ''),
      manifest_signature: f.manifest_signature || (suggestion.placeholders?.manifest_signature ?? ''),
    }));
  };

  const fillDevPlaceholders = (): void => {
    if (!suggestion?.placeholders) return;
    setForm((f) => ({
      ...f,
      image_digest: suggestion.placeholders!.image_digest,
      binary_hash: suggestion.placeholders!.binary_hash,
      manifest_signature: suggestion.placeholders!.manifest_signature,
    }));
  };

  const submit = async (): Promise<void> => {
    setBusy(true); setErr(null);
    try {
      await api.createUpdateRelease({
        version: form.version.trim(),
        image: form.image.trim(),
        image_digest: form.image_digest.trim(),
        binary_hash: form.binary_hash.trim(),
        severity: form.severity,
        type: form.type,
        available_from_tier: form.available_from_tier,
        manifest_signature: form.manifest_signature.trim(),
        min_current_version: form.min_current_version.trim() || null,
        release_notes_url: form.release_notes_url.trim() || null,
        rollout_percent: form.rollout_percent,
      });
      onCreated();
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setBusy(false);
    }
  };

  // Per-step gates: each Next button refuses to advance until the
  // step's required fields are non-empty. The publish button on the
  // final step depends on every gate being satisfied.
  const packageReady = !!(
    form.version.trim() && form.image.trim() &&
    form.image_digest.trim() && form.binary_hash.trim() &&
    form.manifest_signature.trim()
  );
  const classificationReady = !!form.type && !!form.severity && !!form.available_from_tier;
  const allReady = packageReady && classificationReady;

  const stepIndex = WIZARD_STEPS.findIndex((s) => s.id === step);
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === WIZARD_STEPS.length - 1;
  const goPrev = (): void => { if (!isFirst) setStep(WIZARD_STEPS[stepIndex - 1]!.id); };
  const goNext = (): void => { if (!isLast) setStep(WIZARD_STEPS[stepIndex + 1]!.id); };
  const canAdvance =
    step === 'package'        ? packageReady :
    step === 'classification' ? classificationReady :
    /* rollout */                 allReady;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="lg"
      title="Publish release"
      subtitle="Build a package, classify it, then stage the rollout."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          {!isFirst && (
            <Button variant="ghost" size="sm" onClick={goPrev} disabled={busy}>
              Back
            </Button>
          )}
          {!isLast && (
            <Button
              variant="default"
              size="sm"
              onClick={goNext}
              disabled={!canAdvance}
            >
              Next: {WIZARD_STEPS[stepIndex + 1]!.label.replace(/^\d+\.\s/, '')}
            </Button>
          )}
          {isLast && (
            <Button
              variant="default"
              size="sm"
              onClick={() => void submit()}
              disabled={!allReady || busy}
            >
              {busy ? 'Publishing…' : 'Publish release'}
            </Button>
          )}
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <WizardStepper steps={WIZARD_STEPS} current={step} onJump={(id) => {
          // Allow jumping back; jumping forward only when the
          // gate up to that step is satisfied.
          const target = WIZARD_STEPS.findIndex((s) => s.id === id);
          if (target <= stepIndex) setStep(id);
          else if (target === 1 && packageReady) setStep(id);
          else if (target === 2 && packageReady && classificationReady) setStep(id);
        }} />

        {err && <p className="err" style={{ fontSize: '0.8125rem' }}>{err}</p>}

        {step === 'package' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Auto-fill banner — sits above the form so the operator
                can pre-populate before reviewing. */}
            {suggestion && (
              <SuggestionPanel
                suggestion={suggestion}
                onAutoFill={autoFillFromSuggestion}
                onDevPlaceholders={fillDevPlaceholders}
              />
            )}

            <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              These five identity fields are immutable once published — they're what every
              customer pins to. Compute the hashes locally with the build pipeline scripts in
              <code style={{ margin: '0 4px' }}>vloud-commercial/build/scripts/</code>
              and paste them here.
            </p>
            <Field label="Version" hint="Semantic version, e.g. 1.4.2">
              <input value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
                style={inputStyle} />
            </Field>
            <Field label="Image" hint="Container image reference">
              <input value={form.image}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
                style={inputStyle} />
            </Field>
            <Field label="Image digest" hint="sha256:… of the image">
              <input value={form.image_digest}
                onChange={(e) => setForm({ ...form, image_digest: e.target.value })}
                style={inputStyle} />
            </Field>
            <Field label="Binary hash" hint="sha256 of the engine SEA binary">
              <input value={form.binary_hash}
                onChange={(e) => setForm({ ...form, binary_hash: e.target.value })}
                style={inputStyle} />
            </Field>
            <Field label="Manifest signature" hint="Pre-computed JWS over the manifest body">
              <input value={form.manifest_signature}
                onChange={(e) => setForm({ ...form, manifest_signature: e.target.value })}
                style={inputStyle} />
            </Field>
          </div>
        )}

        {step === 'classification' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Severity drives the customer's update policy (auto-apply rules); type drives
              the dashboard banner colour and the per-type auto-update allow-list.
            </p>
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
              <Field label="Severity">
                <select value={form.severity}
                  onChange={(e) => setForm({ ...form, severity: e.target.value as Severity })}
                  style={inputStyle}>
                  <option value="security">security</option>
                  <option value="recommended">recommended</option>
                  <option value="optional">optional</option>
                </select>
              </Field>
              <Field label="Type" hint="Banner color + auto-update allow-list axis.">
                <select value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value as ReleaseType })}
                  style={inputStyle}>
                  {TYPE_OPTIONS.map((t) => (<option key={t} value={t}>{t}</option>))}
                </select>
              </Field>
              <Field label="Available from tier">
                <select value={form.available_from_tier}
                  onChange={(e) => setForm({ ...form, available_from_tier: e.target.value as Tier })}
                  style={inputStyle}>
                  <option value="starter">starter</option>
                  <option value="business">business</option>
                  <option value="enterprise">enterprise</option>
                </select>
              </Field>
              <Field label="Min current version" hint="Skip-from older builds (optional)">
                <input value={form.min_current_version}
                  onChange={(e) => setForm({ ...form, min_current_version: e.target.value })}
                  style={inputStyle} />
              </Field>
            </div>
          </div>
        )}

        {step === 'rollout' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Initial rollout %" hint="Set < 100 to stage; bump later via the per-release slider.">
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: 12, borderRadius: 10,
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
              }}>
                <input type="range" min={0} max={100} step={5}
                  value={form.rollout_percent}
                  onChange={(e) => setForm({ ...form, rollout_percent: Number(e.target.value) })}
                  style={{ flex: 1 }} />
                <span style={{
                  fontSize: '0.875rem', fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  minWidth: 48, textAlign: 'right',
                }}>
                  {form.rollout_percent}%
                </span>
              </div>
            </Field>
            <Field label="Release notes URL" hint="Public link customers see in their banner.">
              <input value={form.release_notes_url}
                onChange={(e) => setForm({ ...form, release_notes_url: e.target.value })}
                style={inputStyle} />
            </Field>

            <ReviewSummary form={form} />
          </div>
        )}
      </div>
    </Drawer>
  );
}

function WizardStepper({
  steps, current, onJump,
}: {
  steps: typeof WIZARD_STEPS;
  current: WizardStep;
  onJump: (id: WizardStep) => void;
}): JSX.Element {
  const idx = steps.findIndex((s) => s.id === current);
  return (
    <ol style={{
      listStyle: 'none', padding: 0, margin: 0,
      display: 'flex', gap: 8,
    }}>
      {steps.map((s, i) => {
        const isCurrent = s.id === current;
        const isPast = i < idx;
        const tone = isCurrent ? '#a78bfa' : isPast ? '#22c55e' : 'var(--text-muted)';
        return (
          <li key={s.id} style={{ flex: 1 }}>
            <button
              type="button"
              onClick={() => onJump(s.id)}
              style={{
                width: '100%',
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
                padding: '8px 10px',
                borderRadius: 10,
                border: `1px solid ${isCurrent ? tone : 'var(--border)'}`,
                background: isCurrent ? `${tone}1a` : 'var(--bg-overlay)',
                cursor: 'pointer',
                textAlign: 'left',
                color: 'inherit',
                fontFamily: 'inherit',
              }}
            >
              <span style={{
                fontSize: '0.75rem', fontWeight: 600,
                color: tone,
              }}>
                {s.label}
              </span>
              <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                {s.hint}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function SuggestionPanel({
  suggestion, onAutoFill, onDevPlaceholders,
}: {
  suggestion: NextReleaseSuggestion;
  onAutoFill: () => void;
  onDevPlaceholders: () => void;
}): JSX.Element {
  const last = suggestion.last;
  const hasPlaceholders = suggestion.placeholders !== null;
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', gap: 10,
        padding: 12,
        borderRadius: 10,
        border: '1px solid rgba(167, 139, 250, 0.35)',
        background:
          'linear-gradient(135deg, rgba(167, 139, 250, 0.10), rgba(34, 211, 238, 0.06))',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.6875rem',
                    color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        <History width={11} height={11} />
        Last published / suggested next
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <KV label="Last version">
          {last
            ? <code>{last.version}</code>
            : <span style={{ color: 'var(--text-muted)' }}>none yet</span>}
        </KV>
        <KV label="Suggested next">
          <code style={{ color: '#a78bfa', fontWeight: 600 }}>{suggestion.suggested_version}</code>
        </KV>
        {last && (
          <KV label="Last image">
            <code style={{ fontSize: '0.6875rem' }}>{last.image}</code>
          </KV>
        )}
        {last && (
          <KV label="Last released">
            {last.released_at.slice(0, 10)}
          </KV>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button variant="default" size="sm" onClick={onAutoFill}>
          <Wand2 width={12} height={12} />
          Auto-fill from suggestion
        </Button>
        {hasPlaceholders && (
          <Button variant="ghost" size="sm" onClick={onDevPlaceholders}>
            <Sparkles width={12} height={12} />
            Generate dev placeholders
          </Button>
        )}
      </div>

      {hasPlaceholders && (
        <p style={{ margin: 0, fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
          {suggestion.placeholders!.note}
        </p>
      )}
      {!hasPlaceholders && (
        <p style={{ margin: 0, fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
          Production mode — image_digest, binary_hash, and manifest_signature must come from your
          CI build pipeline. Auto-fill only sets the version + image template.
        </p>
      )}
    </div>
  );
}

function ReviewSummary({ form }: { form: FormState }): JSX.Element {
  return (
    <div style={{
      padding: 12,
      borderRadius: 10,
      border: '1px solid var(--border)',
      background: 'var(--bg-overlay)',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <h3 style={{
        margin: 0, marginBottom: 4,
        fontSize: '0.6875rem', fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        color: 'var(--text-muted)',
      }}>
        Review
      </h3>
      <ReviewRow k="Version" v={form.version || <em style={{ color: 'var(--danger)' }}>missing</em>} />
      <ReviewRow k="Image" v={form.image || <em style={{ color: 'var(--danger)' }}>missing</em>} />
      <ReviewRow k="Digest" v={form.image_digest ? <code>{form.image_digest.slice(0, 24)}…</code> : <em style={{ color: 'var(--danger)' }}>missing</em>} />
      <ReviewRow k="Binary hash" v={form.binary_hash ? <code>{form.binary_hash.slice(0, 24)}…</code> : <em style={{ color: 'var(--danger)' }}>missing</em>} />
      <ReviewRow k="Severity" v={form.severity} />
      <ReviewRow k="Type" v={typeChip(form.type)} />
      <ReviewRow k="Tier" v={form.available_from_tier} />
      <ReviewRow k="Rollout" v={`${form.rollout_percent}%${form.rollout_percent < 100 ? ' (staged)' : ''}`} />
      {form.min_current_version && <ReviewRow k="Min current" v={form.min_current_version} />}
      {form.release_notes_url && <ReviewRow k="Notes URL" v={<a href={form.release_notes_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent-text)' }}>{form.release_notes_url}</a>} />}
    </div>
  );
}

function ReviewRow({ k, v }: { k: string; v: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, fontSize: '0.75rem' }}>
      <span style={{ minWidth: 96, color: 'var(--text-muted)' }}>{k}</span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Edit release drawer (PATCH a subset of fields)
 *
 * Only the safe-to-edit fields are exposed here — version, image,
 * image_digest, binary_hash, and manifest_signature are deliberately
 * NOT editable (changing them would invalidate every customer that
 * pinned to those hashes). Staff who need to "fix" an immutable
 * field publish a new version + yank the old one.
 * ────────────────────────────────────────────────────────────────── */

function EditDrawer({
  api, version, rows, onClose, onSaved,
}: {
  api: AdminApiClient;
  version: string | null;
  rows: UpdateReleaseRow[];
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const original = useMemo(
    () => rows.find((r) => r.version === version) ?? null,
    [rows, version],
  );
  const [severity, setSeverity] = useState<Severity>('recommended');
  const [releaseType, setReleaseType] = useState<ReleaseType>('PATCH');
  const [availableFromTier, setAvailableFromTier] = useState<Tier>('starter');
  const [minCurrent, setMinCurrent] = useState('');
  const [notesUrl, setNotesUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset form whenever a different release is opened.
  useEffect(() => {
    if (!original) return;
    setSeverity(original.severity);
    setReleaseType(original.type ?? 'PATCH');
    setAvailableFromTier(original.available_from_tier);
    setMinCurrent(original.min_current_version ?? '');
    setNotesUrl(original.release_notes_url ?? '');
    setErr(null);
  }, [original?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (): Promise<void> => {
    if (!original) return;
    setBusy(true); setErr(null);
    try {
      // Only send fields whose value actually changed. The PATCH
      // endpoint rejects an empty body — so as long as something
      // changed, we'll have at least one key.
      const patch: Parameters<typeof api.editUpdateRelease>[1] = {};
      if (severity !== original.severity) patch.severity = severity;
      if (releaseType !== original.type) patch.type = releaseType;
      if (availableFromTier !== original.available_from_tier) patch.available_from_tier = availableFromTier;
      if ((minCurrent || null) !== original.min_current_version) patch.min_current_version = minCurrent || null;
      if ((notesUrl || null) !== original.release_notes_url) patch.release_notes_url = notesUrl || null;
      if (Object.keys(patch).length === 0) {
        onSaved();
        return;
      }
      await api.editUpdateRelease(original.version, patch);
      onSaved();
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={!!original}
      onClose={onClose}
      width="md"
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Pencil width={14} height={14} />
          Edit release
        </span>
      }
      subtitle={original ? `${original.version} — non-identity fields only` : undefined}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="default" size="sm" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      {original && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {err && <p className="err" style={{ fontSize: '0.8125rem' }}>{err}</p>}
          <Field label="Severity">
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as Severity)}
              style={inputStyle}
            >
              <option value="security">security</option>
              <option value="recommended">recommended</option>
              <option value="optional">optional</option>
            </select>
          </Field>
          <Field label="Type" hint="Drives the customer banner colour + auto-update allow-list.">
            <select
              value={releaseType}
              onChange={(e) => setReleaseType(e.target.value as ReleaseType)}
              style={inputStyle}
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>
          <Field label="Available from tier">
            <select
              value={availableFromTier}
              onChange={(e) => setAvailableFromTier(e.target.value as Tier)}
              style={inputStyle}
            >
              <option value="starter">starter</option>
              <option value="business">business</option>
              <option value="enterprise">enterprise</option>
            </select>
          </Field>
          <Field label="Min current version" hint="Leave blank for any.">
            <input
              value={minCurrent}
              onChange={(e) => setMinCurrent(e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="Release notes URL">
            <input
              value={notesUrl}
              onChange={(e) => setNotesUrl(e.target.value)}
              style={inputStyle}
            />
          </Field>
          <p
            style={{
              margin: 0, fontSize: '0.6875rem', color: 'var(--text-muted)',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <Lock width={11} height={11} />
            version, image, image_digest, binary_hash, manifest_signature are immutable.
          </p>
        </div>
      )}
    </Drawer>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Cut-release card — panel-driven: pick major/minor/patch, the server
 * computes the version + queues the signed build.
 * ────────────────────────────────────────────────────────────────── */

const CHANNELS: Array<ReleaseCandidate['channel']> = ['beta', 'rc', 'stable', 'alpha', 'internal'];

function CutReleaseCard({
  api, candidate: initial, onCut, refreshSignal,
}: {
  api: AdminApiClient;
  candidate: ReleaseCandidate;
  onCut: () => void;
  refreshSignal: number;
}): JSX.Element {
  const [channel, setChannel] = useState<string>(initial.channel || 'beta');
  const [cand, setCand] = useState<ReleaseCandidate>(initial);
  const [busy, setBusy] = useState<null | 'major' | 'minor' | 'patch'>(null);
  const [queued, setQueued] = useState<{ version: string; buildRequestId: string | null } | null>(null);
  // Why the last cut failed, straight from the runner. Kept separate from
  // `err` (the API call itself failing) so the two are never confused.
  const [buildErr, setBuildErr] = useState<{ version: string; detail: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ bump: 'major' | 'minor' | 'patch'; version: string } | null>(null);

  // Recompute next-versions when the channel changes, and re-fetch whenever
  // the parent refreshes (refreshSignal bumps after a publish/cut/yank). This
  // is what disables the bump buttons the instant a draft is published —
  // has_new_code flips to false server-side — with no manual page reload.
  useEffect(() => {
    let live = true;
    api.releaseCandidate(channel).then((c) => { if (live) setCand(c); }).catch(() => {});
    return () => { live = false; };
  }, [api, channel, refreshSignal]);

  const cut = async (bump: 'major' | 'minor' | 'patch'): Promise<void> => {
    setBusy(bump); setErr(null); setQueued(null); setBuildErr(null);
    try {
      const r = await api.cutRelease(bump, channel);
      setQueued({ version: r.version, buildRequestId: r.build_request_id });
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setBusy(null);
    }
  };

  // While a build is queued, poll for the draft to actually land (the
  // build takes a few minutes). When it appears, clear the "Building"
  // banner and refresh the lane — no manual page refresh needed.
  // Poll the BUILD REQUEST, not just the release list.
  //
  // Watching only for the version to appear cannot tell a slow build from a
  // dead one: a build that failed in sixty seconds looked identical to one
  // still running, and the banner just vanished after twelve minutes with
  // nothing said. The runner records why it failed -- status=failed plus the
  // tail of the build log -- so read that and show it.
  useEffect(() => {
    if (!queued) return;
    let live = true;
    const started = Date.now();
    const iv = setInterval(async () => {
      if (!live) return;
      try {
        if (queued.buildRequestId) {
          const br = await api.buildRequest(queued.buildRequestId);
          if (br.status === 'failed') {
            if (live) {
              setBuildErr({ version: queued.version, detail: br.detail ?? 'The build failed and reported no detail.' });
              setQueued(null);
            }
            return;
          }
        }
        const { releases } = await api.updateReleases();
        if (releases.some((r) => r.version === queued.version)) {
          if (live) { setQueued(null); onCut(); }
        }
      } catch { /* keep polling */ }
      // Safety stop, now only for the case where the runner never answers at
      // all -- a reported failure ends the poll above, immediately.
      if (Date.now() - started > 12 * 60 * 1000) {
        if (live) {
          setBuildErr({
            version: queued.version,
            detail: 'No result after 12 minutes. Check that the build runner is running and polling '
              + '/ci/build-requests/next (systemctl status vloud-build-runner).',
          });
          setQueued(null);
        }
      }
    }, 12000);
    return () => { live = false; clearInterval(iv); };
  }, [queued]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitLine = cand.latest_commit?.message?.split('\n')[0] ?? '(no commits found)';

  return (
    <div
      style={{
        marginTop: 16,
        border: '1px solid var(--border)',
        borderRadius: 12,
        background: 'var(--bg-panel, transparent)',
        padding: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Plus width={16} height={16} style={{ color: '#34d399' }} />
        <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600 }}>Cut a release</h3>
        {cand.has_new_code
          ? <StatusChip band="healthy">new code on main</StatusChip>
          : <StatusChip band="neutral">no new commits</StatusChip>}
      </div>
      <p style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        <GitCommit width={12} height={12} />
        <span>
          current <code>{cand.current_version}</code> · latest commit: {commitLine}
          {cand.latest_commit?.author ? ` — ${cand.latest_commit.author}` : ''}
        </span>
      </p>

      {err && <p className="err" style={{ fontSize: '0.8125rem' }}>{err}</p>}

      {queued ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem' }}>
          <RefreshCw width={14} height={14} style={{ animation: 'spin 900ms linear infinite' }} />
          Building <strong>{queued.version}</strong> — it'll appear below as a draft in a few minutes.
        </div>
        ) : buildErr ? (
          <div
            style={{
              display: 'flex', flexDirection: 'column', gap: 6,
              padding: '10px 12px', borderRadius: 8, fontSize: '0.8125rem',
              border: '1px solid color-mix(in srgb, #ef4444 45%, var(--border))',
              background: 'color-mix(in srgb, #ef4444 8%, transparent)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
              <span><strong>{buildErr.version}</strong> failed to build — no draft was created.</span>
              <button
                type="button"
                onClick={() => setBuildErr(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.7 }}
              >
                dismiss
              </button>
            </div>
            <pre
              style={{
                margin: 0, maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap',
                wordBreak: 'break-word', fontFamily: 'ui-monospace, monospace',
                fontSize: '0.75rem', opacity: 0.9,
              }}
            >
              {buildErr.detail}
            </pre>
          </div>
      ) : confirm ? (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            padding: '10px 12px', borderRadius: 8,
            border: '1px solid color-mix(in srgb, #f59e0b 45%, var(--border))',
            background: 'color-mix(in srgb, #f59e0b 8%, transparent)',
          }}
        >
          <span style={{ fontSize: '0.8125rem' }}>
            Build + sign <strong>{confirm.version}</strong> now? This runs a real signed build
            on Azure and uploads it to the release blob (~4 min). It lands as a draft — nothing
            reaches customers until you publish it.
          </span>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <Button variant="ghost" size="sm" disabled={!!busy} onClick={() => setConfirm(null)}>Cancel</Button>
            <Button variant="default" size="sm" disabled={!!busy}
                    onClick={() => { const b = confirm.bump; setConfirm(null); void cut(b); }}>
              {busy ? 'Queuing…' : `Build & sign ${confirm.version}`}
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Channel
            <select value={channel} onChange={(e) => setChannel(e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '6px 8px' }}>
              {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          {(['major', 'minor', 'patch'] as const).map((b) => (
            <Button key={b} variant={b === 'patch' ? 'default' : 'outline'} size="sm"
                    disabled={!cand.has_new_code}
                    title={!cand.has_new_code ? 'No new commits since the last release — nothing to build' : undefined}
                    onClick={() => setConfirm({ bump: b, version: cand.next[b] })}>
              {`${b.charAt(0).toUpperCase()}${b.slice(1)} → ${cand.next[b]}`}
            </Button>
          ))}
          {!cand.has_new_code && (
            <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
              No new commits since the last release — push code to cut a new one.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Draft lane — CI-registered releases awaiting classification
 * ────────────────────────────────────────────────────────────────── */

function DraftReleasesLane({
  rows, superseded, canWrite, onPublish, onDiscard,
}: {
  rows: UpdateReleaseRow[];
  canWrite: boolean;
  superseded: Set<string>;
  onPublish: (v: string) => void;
  onDiscard: (v: string) => void;
}): JSX.Element {
  const [confirmDiscard, setConfirmDiscard] = useState<string | null>(null);
  return (
    <div
      style={{
        marginTop: 16,
        border: '1px solid color-mix(in srgb, #a78bfa 40%, var(--border))',
        borderRadius: 12,
        background: 'color-mix(in srgb, #a78bfa 7%, var(--bg-panel, transparent))',
        padding: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <GitCommit width={16} height={16} style={{ color: '#a78bfa' }} />
        <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600 }}>
          Detected from CI — classify &amp; publish
        </h3>
        <StatusChip band="warning">{rows.length} draft{rows.length === 1 ? '' : 's'}</StatusChip>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        The pipeline built, signed, and uploaded these versions. They are invisible to every
        customer until an Owner or Admin sets the type + rollout and publishes.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r) => (
          <div
            key={r.version}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, flexWrap: 'wrap',
              padding: '10px 12px', borderRadius: 8,
              border: '1px solid var(--border)', background: 'var(--bg-overlay)',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{r.version}</span>
                <StatusChip band="neutral">{r.channel}</StatusChip>
                {r.manifest_signature && (
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 3,
                             fontSize: '0.6875rem', color: 'var(--text-muted)' }}
                  >
                    <Lock width={11} height={11} /> signed
                  </span>
                )}
                {superseded.has(r.version) && (
                  <StatusChip band="warning">superseded — older than a published release</StatusChip>
                )}
              </div>
              <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                detected {fmtRelative(r.released_at)} · {r.image_digest.slice(0, 20)}…
              </span>
              {r.commit_message && (
                <span style={{ fontSize: '0.75rem', color: 'var(--text-primary)', marginTop: 2 }}>
                  {r.commit_message.split('\n')[0]}
                </span>
              )}
            </div>
            {!canWrite ? null : confirmDiscard === r.version ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Discard this draft?</span>
                <Button variant="ghost" size="sm" onClick={() => setConfirmDiscard(null)}>Cancel</Button>
                <Button variant="destructive" size="sm"
                        onClick={() => { setConfirmDiscard(null); onDiscard(r.version); }}>
                  Discard
                </Button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Button variant="ghost" size="sm" onClick={() => setConfirmDiscard(r.version)}>
                  <ShieldX width={14} height={14} /> Discard
                </Button>
                <Button variant="default" size="sm"
                        disabled={superseded.has(r.version)}
                        title={superseded.has(r.version) ? 'A newer version is already published — discard this one' : undefined}
                        onClick={() => onPublish(r.version)}>
                  <Wand2 width={14} height={14} />
                  Classify &amp; publish
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Publish-draft drawer — operator sets classification + rollout, then
 * flips the draft live via PUT /internal/update-releases/:v/publish.
 * ────────────────────────────────────────────────────────────────── */

function PublishDraftDrawer({
  api, version, rows, onClose, onPublished,
}: {
  api: AdminApiClient;
  version: string | null;
  rows: UpdateReleaseRow[];
  onClose: () => void;
  onPublished: () => void;
}): JSX.Element {
  const draft = useMemo(
    () => rows.find((r) => r.version === version) ?? null,
    [rows, version],
  );
  const [releaseType, setReleaseType] = useState<ReleaseType>('ENGINE');
  const [severity, setSeverity] = useState<Severity>('recommended');
  const [availableFromTier, setAvailableFromTier] = useState<Tier>('starter');
  const [rollout, setRollout] = useState(100);
  const [minCurrent, setMinCurrent] = useState('');
  const [notesUrl, setNotesUrl] = useState('');
  const [mandatory, setMandatory] = useState(false);
  const [promoteLatest, setPromoteLatest] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!draft) return;
    setReleaseType(draft.type ?? 'ENGINE');
    setSeverity(draft.severity ?? 'recommended');
    setAvailableFromTier(draft.available_from_tier ?? 'starter');
    setRollout(100);
    setMinCurrent(draft.min_current_version ?? '');
    setNotesUrl(draft.release_notes_url ?? '');
    setMandatory(false);
    setPromoteLatest(true);
    setErr(null);
  }, [draft?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (): Promise<void> => {
    if (!draft) return;
    setBusy(true); setErr(null);
    try {
      await api.publishDraftRelease(draft.version, {
        type: releaseType,
        severity,
        available_from_tier: availableFromTier,
        rollout_percent: rollout,
        min_current_version: minCurrent || null,
        release_notes_url: notesUrl || null,
        mandatory,
        promote_latest: promoteLatest,
      });
      onPublished();
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={!!draft}
      onClose={onClose}
      width="md"
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Wand2 width={14} height={14} />
          Classify &amp; publish
        </span>
      }
      subtitle={draft ? `${draft.version} — set classification + rollout, then go live` : undefined}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="default" size="sm" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Publishing…' : 'Publish to fleet'}
          </Button>
        </>
      }
    >
      {draft && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {err && <p className="err" style={{ fontSize: '0.8125rem' }}>{err}</p>}

          <div
            style={{
              padding: 10, borderRadius: 8,
              border: '1px solid var(--border)', background: 'var(--bg-overlay)',
              fontSize: '0.6875rem', color: 'var(--text-muted)',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Lock width={11} height={11} /> Identity (from CI, immutable)
            </span>
            <span>digest {draft.image_digest.slice(0, 28)}…</span>
            <span>channel {draft.channel}{draft.manifest_signature ? ' · signed' : ''}</span>
            {draft.commit_message && (
              <span style={{ color: 'var(--text-primary)' }}>
                description (from commit): {draft.commit_message.split('\n')[0]}
              </span>
            )}
          </div>

          <Field label="Type" hint="Drives the customer banner colour + auto-update allow-list.">
            <select value={releaseType} onChange={(e) => setReleaseType(e.target.value as ReleaseType)} style={inputStyle}>
              {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Severity">
            <select value={severity} onChange={(e) => setSeverity(e.target.value as Severity)} style={inputStyle}>
              <option value="security">security</option>
              <option value="recommended">recommended</option>
              <option value="optional">optional</option>
            </select>
          </Field>
          <Field label="Available from tier" hint="Minimum plan that sees this release. 'All installs' = everyone.">
            <select value={availableFromTier} onChange={(e) => setAvailableFromTier(e.target.value as Tier)} style={inputStyle}>
              <option value="starter">All installs (any tier)</option>
              <option value="business">Business and up</option>
              <option value="enterprise">Enterprise only</option>
            </select>
          </Field>
          <Field label={`Initial rollout — ${rollout}%`} hint="Stage lower (e.g. 25) to canary; bump later on the release drawer.">
            <input
              type="range" min={0} max={100} step={5}
              value={rollout}
              onChange={(e) => setRollout(parseInt(e.target.value, 10))}
              style={{ width: '100%' }}
            />
          </Field>
          <Field label="Min current version" hint="Leave blank for any.">
            <input value={minCurrent} onChange={(e) => setMinCurrent(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Release notes URL">
            <input value={notesUrl} onChange={(e) => setNotesUrl(e.target.value)} style={inputStyle} />
          </Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} />
            Mandatory — engine force-applies past deadline
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.8125rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={promoteLatest} onChange={(e) => setPromoteLatest(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              Set as fresh-install download
              <span style={{ display: 'block', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                also points <code>curl install.vloud.app | bash</code> at this version (a ~2-min
                promote build re-signs the download). Leave on unless this is an old/targeted fix.
              </span>
            </span>
          </label>
        </div>
      )}
    </Drawer>
  );
}

/* Helpers */

function Section({
  title, hint, children,
}: { title: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <section>
      <h3
        style={{
          margin: 0, marginBottom: 8,
          fontSize: '0.6875rem', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          color: 'var(--text-muted)',
        }}
      >
        {title}
      </h3>
      {children}
      {hint && (
        <p style={{ margin: '6px 0 0', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
          {hint}
        </p>
      )}
    </section>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{label}</span>
      <div style={{ fontSize: '0.8125rem' }}>{children}</div>
    </div>
  );
}

function Field({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{hint}</span>
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: 8,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg-overlay)',
  color: 'var(--text-primary)',
  fontSize: '0.8125rem',
  outline: 'none',
  fontFamily: 'inherit',
} as const;

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
